import { useState, useMemo, useCallback } from "react"
import { getWeekDates } from "../utils/calendarHelpers"

/**
 * Custom hook for managing date navigation and formatting
 * @param {string} viewMode - Current view mode (day/week/month)
 * @returns {Object} - Date state and navigation functions
 */
export const useDateNavigation = (viewMode) => {
    const [selectedDate, setSelectedDate] = useState(new Date())

    // Navigate to previous/next day/week/month
    const navigateDate = useCallback((direction) => {
        setSelectedDate(prev => {
            const newDate = new Date(prev)
            if (viewMode === "day") {
                newDate.setDate(newDate.getDate() + direction)
            } else if (viewMode === "week") {
                newDate.setDate(newDate.getDate() + direction * 7)
            } else {
                newDate.setMonth(newDate.getMonth() + direction)
            }
            return newDate
        })
    }, [viewMode])

    // Go to today
    const goToToday = useCallback(() => {
        setSelectedDate(new Date())
    }, [])

    // Format date display based on view mode
    const formatDateDisplay = useMemo(() => {
        if (viewMode === "week") {
            const weekDates = getWeekDates(selectedDate)
            const startDate = weekDates[0]
            const endDate = weekDates[6]

            const options = { month: "long", day: "numeric" }
            const year = endDate.getFullYear()

            return `${startDate.toLocaleDateString("en-US", options)}-${endDate.getDate()}, ${year}`
        }

        if (viewMode === "month") {
            const options = { month: "long", year: "numeric" }
            return selectedDate.toLocaleDateString("en-US", options)
        }

        const options = { month: "long", day: "numeric", year: "numeric" }
        return selectedDate.toLocaleDateString("en-US", options)
    }, [selectedDate, viewMode])

    // Format journal date (e.g., "Saturday, August 19 Journal")
    const formatJournalDate = useMemo(() => {
        const options = { weekday: "long", month: "long", day: "numeric" }
        return `${selectedDate.toLocaleDateString("en-US", options)} Journal`
    }, [selectedDate])

    return {
        selectedDate,
        setSelectedDate,
        navigateDate,
        goToToday,
        formatDateDisplay,
        formatJournalDate
    }
}
